import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import NuevoTurnoModal from "@/components/NuevoTurnoModal";
import { Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API = "/api";

const EstadoBadge = ({ estado }) => {
  const cls = {
    pendiente: "badge-pendiente",
    confirmado: "badge-confirmado",
    completado: "badge-completado",
    cancelado: "badge-cancelado",
  };
  return <span className={cls[estado] || "badge-pendiente"}>{estado || "pendiente"}</span>;
};

const PAGE_SIZE = 15;

export default function TurnosPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [turnos, setTurnos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTurno, setEditTurno] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      if (search) params.set("search", search);
      if (filterEstado) params.set("estado", filterEstado);
      const res = await axios.get(`${API}/turnos?${params}`, { headers: getAuthHeader() });
      setTurnos(res.data);
      setTotal(res.data.length + page * PAGE_SIZE);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterEstado, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [search, filterEstado]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este turno?")) return;
    try {
      await axios.delete(`${API}/turnos/${id}`, { headers: getAuthHeader() });
      toast.success("Turno eliminado");
      load();
    } catch {
      toast.error("Error al eliminar turno");
    }
  };

  const handleEdit = (turno, e) => {
    e.stopPropagation();
    setEditTurno(turno);
    setShowModal(true);
  };

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Turnos</h1>
          <p className="text-slate-500 text-sm mt-0.5">Gestión de todos los turnos</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => { setEditTurno(null); setShowModal(true); }}
          data-testid="nuevo-turno-list-btn"
        >
          <Plus size={16} />
          Nuevo turno
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-52">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, apellido, DNI..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-10 !py-2.5"
            data-testid="search-turnos"
          />
        </div>
        <select
          value={filterEstado}
          onChange={(e) => setFilterEstado(e.target.value)}
          className="input-field !w-auto !py-2.5"
          data-testid="filter-estado"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="confirmado">Confirmado</option>
          <option value="completado">Completado</option>
          <option value="cancelado">Cancelado</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-50">
                <th className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-400">Paciente</th>
                <th className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-400">DNI</th>
                <th className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-400">Fecha / Hora</th>
                <th className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-400">Servicio</th>
                <th className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-400">Cobertura</th>
                <th className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-400">Estado</th>
                <th className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-400">Pago</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(6).fill(0).map((_, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    {Array(7).fill(0).map((__, j) => (
                      <td key={j} className="px-5 py-4">
                        <div className="h-4 bg-slate-100 rounded animate-pulse" />
                      </td>
                    ))}
                    <td className="px-5 py-4" />
                  </tr>
                ))
              ) : turnos.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-slate-400 text-sm">
                    {search || filterEstado ? "No se encontraron resultados" : "No hay turnos registrados"}
                  </td>
                </tr>
              ) : (
                turnos.map((t) => (
                  <tr
                    key={t.id}
                    className="border-b border-slate-50 turno-row cursor-pointer"
                    onClick={() => t.dni && navigate(`/paciente/${t.dni}`)}
                    data-testid="turno-table-row"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-700 font-bold text-xs">
                            {(t.nombre?.[0] || "?").toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {t.nombre} {t.apellido}
                          </p>
                          {t.telefono && <p className="text-xs text-slate-400">{t.telefono}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">{t.dni || "-"}</td>
                    <td className="px-5 py-4">
                      <p className="text-sm text-slate-900">{t.fecha_turno || "-"}</p>
                      {t.hora_turno && <p className="text-xs text-slate-400">{t.hora_turno}</p>}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">{t.servicio || "-"}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">{t.cobertura || "Particular"}</td>
                    <td className="px-5 py-4">
                      <EstadoBadge estado={t.estado} />
                    </td>
                    <td className="px-5 py-4">
                      {t.pago_completado ? (
                        <span className="badge-completado">Pagado{t.monto_pago ? ` $${t.monto_pago}` : ""}</span>
                      ) : (
                        <span className="badge-pendiente">Pendiente</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => handleEdit(t, e)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                          data-testid="edit-turno-btn"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={(e) => handleDelete(t.id, e)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                          data-testid="delete-turno-btn"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-50">
          <p className="text-xs text-slate-400">
            Mostrando {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + turnos.length} resultados
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 text-slate-600 transition-colors"
              data-testid="prev-page-btn"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs font-medium text-slate-600">Pág. {page + 1}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={turnos.length < PAGE_SIZE}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 text-slate-600 transition-colors"
              data-testid="next-page-btn"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <NuevoTurnoModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditTurno(null); }}
        onSuccess={load}
        turnoEdit={editTurno}
      />
    </div>
  );
}
